import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { X, LogOut, Shield } from "lucide-react";
import BrandLogo from "./BrandLogo";

/**
 * Slide-in mobile drawer showing ALL nav destinations for the current role.
 * Used for vet/store/hostel/NGO/admin (they have >5 sections) and parent (16 side-links).
 * Auto-closes on route change (parent handles via `onClose` in NavLink click).
 */
export default function MobileNavDrawer({ open, onClose, tabs = [], sideLinks = [], role = "parent", user, onLogout }) {
  // Lock scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const roleLabel = {
    vet: "Vet Portal", store_owner: "Store Portal", hostel_owner: "Hostel Portal",
    ngo: "NGO Portal", groomer: "Groomer", walker: "Walker", super_admin: "Admin",
    parent: "Pet Parent",
  }[role] || "PetBond";

  return (
    <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-drawer">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="mobile-drawer-backdrop"
      />
      {/* Panel */}
      <aside
        className="absolute right-0 top-0 bottom-0 w-[84vw] max-w-[340px] bg-bg shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200 flex flex-col"
        data-testid="mobile-drawer-panel"
      >
        <header className="sticky top-0 bg-bg/95 backdrop-blur-xl border-b border-line px-4 py-3 flex items-center justify-between">
          <BrandLogo variant="full" size="sm" to="/app" />
          <button
            onClick={onClose}
            data-testid="mobile-drawer-close"
            className="w-11 h-11 -mr-2 rounded-full grid place-items-center hover:bg-surface-muted active:bg-surface-muted"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-4 py-4 border-b border-line flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-11 h-11 rounded-full object-cover border border-line" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-leaf grid place-items-center font-bold text-ink">
              {user?.name?.[0] ?? "P"}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-sm truncate">{user?.name || "Signed in"}</div>
            <div className="text-[11px] font-bold tracking-wider uppercase text-brand">{roleLabel}</div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {tabs.map(t => (
            <DrawerLink key={t.to} to={t.to} end={t.end} icon={t.icon} label={t.label} onClose={onClose} testid={`drawer-${t.testid || t.label.toLowerCase()}`} />
          ))}
          {sideLinks.length > 0 && (
            <>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink-soft px-3 pt-4 pb-1.5">More</div>
              {sideLinks.map(s => (
                <DrawerLink key={s.to} to={s.to} icon={s.icon} label={s.label} onClose={onClose} testid={`drawer-side-${s.label.toLowerCase().replace(/[^a-z]+/g,'-')}`} />
              ))}
            </>
          )}
        </nav>

        <footer className="sticky bottom-0 bg-bg/95 backdrop-blur-xl border-t border-line px-2 py-2 space-y-0.5">
          <DrawerLink to="/app/privacy-data" icon={Shield} label="Privacy & Data" onClose={onClose} testid="drawer-privacy" />
          <button
            onClick={() => { onClose(); onLogout?.(); }}
            data-testid="drawer-logout"
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-bold text-ink-soft hover:bg-surface-muted active:bg-surface-muted transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </footer>
      </aside>
    </div>
  );
}

function DrawerLink({ to, end, icon: Icon, label, onClose, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClose}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-bold transition-colors min-h-[44px] ${
          isActive ? "bg-ink text-white" : "text-ink hover:bg-surface-muted active:bg-surface-muted"
        }`
      }
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
